import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import TargetDoseBadge from '~/components/DoseItem/TargetDoseBadge';
import InlineEdit from '~/components/InlineEdit';
import { DICOMApprovalStatus } from '~/domain/dicom/dicomConstants';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import useDoseTargetLookup from '~/hooks/patient/dose/useDoseTargetLookup';
import useStructureNameValidation from '~/hooks/structure-set/useStructureNameValidation';
import { renamingRoiState } from '~/state/atoms/structureSet/roiAtoms';
import StructureActions from './Actions';
import DensityIndicator from './DensityIndicator';

export default function StructureField({
    roi,
    onNameChange,
    isEditable,
    isSelected,
    disabled = false,
    approvalStatus,
}: {
    roi: RoiReadModel;
    onNameChange: (value: string) => void;
    isEditable: boolean;
    isSelected: boolean;
    disabled?: boolean;
    approvalStatus?: DICOMApprovalStatus;
}) {
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const renamingRoi = useRecoilValue(renamingRoiState);
    const setRenamingRoi = useSetRecoilState(renamingRoiState);
    const validate = useStructureNameValidation(roi);

    useEffect(() => {
        if (renamingRoi === roi.roiNumber) {
            setIsEditing(true);
            setRenamingRoi(null);
        }
    }, [renamingRoi, roi.roiNumber, setRenamingRoi]);

    const targetDoseLookup = useDoseTargetLookup();
    const targetDoseGy = targetDoseLookup.get(roi.name);
    const isTarget = targetDoseGy !== undefined;

    return isEditing ? (
        <InlineEdit
            value={roi.name}
            onSubmit={onNameChange}
            onCancel={() => setIsEditing(false)}
            validate={validate}
            fontSize={12}
            tooltipOffset={[-4, -12]}
        />
    ) : (
        <Box
            display="grid"
            alignItems="center"
            width="100%"
            sx={{
                gridTemplateColumns: '1fr 20px auto',
                gap: 0.5,
                '&:hover .checkbox-container': {
                    visibility: 'visible',
                },
                '&:hover .structure-name': {
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                },
            }}
        >
            <Box display="flex" alignItems="center" gap={0.5} minWidth={0}>
                <Typography
                    className="structure-name"
                    variant="body2"
                    fontSize={12}
                    fontWeight={isTarget ? 500 : undefined}
                    data-testid={`structure-name-${roi.name}`}
                    color={disabled ? 'text.disabled' : 'text.primary'}
                    onDoubleClick={
                        isEditable ? () => setIsEditing(true) : undefined
                    }
                    sx={{
                        minWidth: 0,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        cursor: isEditable ? 'pointer' : undefined,
                    }}
                >
                    {roi.name}
                </Typography>
                {isTarget && (
                    <TargetDoseBadge
                        doseQuantityGy={targetDoseGy}
                        structureName={roi.name}
                        fontSize={10}
                    />
                )}
            </Box>
            <DensityIndicator roi={roi} />
            <StructureActions
                roi={roi}
                isSelected={isSelected}
                approvalStatus={approvalStatus}
            />
        </Box>
    );
}
